import React from 'react'
import { Breadcrumbs, Chip, Tooltip } from '@mui/material'
import { emphasize, styled } from '@mui/material/styles'
import HomeIcon from '@mui/icons-material/Home'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Link as RouterLink } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useTypedSelector } from '../../reducers'
import { getCurrentTenant } from '../../state'
import useBreadcrumbTrail from '../../hooks/use-breadcrumb-trail.hook'
import { trailState } from '../../helpers/breadcrumbTrail'

// Chip's label already ellipsises, so capping the chip is enough to truncate
// long folder names.
const StyledCrumb = styled(Chip)(({ theme }) => ({
  maxWidth: '15vw',
  height: theme.spacing(3),
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightRegular,
  '&:hover, &:focus': {
    backgroundColor: theme.palette.grey[300]
  },
  '&:active': {
    boxShadow: theme.shadows[1],
    backgroundColor: emphasize(theme.palette.grey[200], 0.12)
  }
})) as typeof Chip

// The site crumb mirrors the site chip in the page header (AppLayout): the
// secondary colour with white text, at breadcrumb size.
const StyledSiteCrumb = styled(StyledCrumb)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: '#ffffff',
  '& .MuiChip-icon': {
    color: 'inherit'
  },
  '&:hover, &:focus': {
    backgroundColor: theme.palette.secondary.dark
  },
  '&:active': {
    backgroundColor: theme.palette.secondary.dark
  }
})) as typeof Chip

interface ICrumbView {
  key: string
  name: string
  to: string
  state?: ReturnType<typeof trailState>
  role?: string
  roleLoading: boolean
}

const AppBreadcrumbNav = ({
  allowLastLink = false
}: {
  allowLastLink?: boolean
}) => {
  const intl = useIntl()
  const { trail, roles, complete } = useBreadcrumbTrail()

  // The tenant list carries the user's own site role, so it is right on every
  // page; customerUsers is loaded per folder on folder pages and is not.
  const tenant = useTypedSelector<
    { id: string; name?: string; role?: string } | undefined
  >(getCurrentTenant)

  const crumbs: ICrumbView[] = [
    {
      key: 'tenant',
      name: tenant?.name || '',
      to: '/site',
      role: (tenant && roles?.[tenant.id]) || tenant?.role || undefined,
      roleLoading: false
    },
    ...trail.map((crumb, index) => ({
      key: crumb.id,
      name: crumb.name,
      to: `/project/${crumb.id}`,
      // A partial trail (hard load before /path answers) would read as the
      // full ancestry on arrival, so only a complete one is carried.
      state: complete ? trailState(trail.slice(0, index + 1)) : undefined,
      role: roles?.[crumb.id],
      roleLoading: roles === null
    }))
  ]

  const tooltip = ({ name, role, roleLoading }: ICrumbView) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {name}
      {role ? (
        <Chip size="small" variant="outlined" color="primary" label={role} />
      ) : roleLoading ? (
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={intl.formatMessage({
            id: 'breadcrumbs.roleLoading',
            defaultMessage: 'Loading…'
          })}
        />
      ) : null}
    </div>
  )

  return (
    <Breadcrumbs
      maxItems={4}
      aria-label="breadcrumb"
      separator={<NavigateNextIcon fontSize="small" />}
    >
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1
        const site = index === 0
        const Crumb = site ? StyledSiteCrumb : StyledCrumb
        const icon = site ? <HomeIcon fontSize="small" /> : undefined

        return (
          <Tooltip key={crumb.key} title={tooltip(crumb)}>
            {last && !allowLastLink ? (
              <Crumb
                label={crumb.name}
                icon={icon}
                tabIndex={0}
                sx={{ fontWeight: 'bold' }}
              />
            ) : (
              <Crumb
                component={RouterLink}
                to={crumb.to}
                state={crumb.state}
                label={crumb.name}
                icon={icon}
                clickable
                onClick={event => event.stopPropagation()}
              />
            )}
          </Tooltip>
        )
      })}
    </Breadcrumbs>
  )
}

export default AppBreadcrumbNav
